// external library imports
import React, { useState, useContext, useRef } from 'react'
import classNames from 'classnames'
import { isEmpty, has } from 'lodash-es'
import { A11yDialog } from 'react-a11y-dialog'
import DateFormat from '../../shared/helpers/date-format'

// React components
import AddOnParty from '../views/add-on-party'
// import Tag from './tag'
import EventTime from './event-time'
import PricingBlock from './pricing-block'

// helper methods
import { inCart, anyMembershipTickets, inManualUpdate } from '../helpers/logic'
import { extractPricing } from '../../shared/helpers/logic'

// React app contexts
import { TicketsAppContext } from '../context/tickets-app-context'
import { OldCartContext } from '../context/old-cart-context'

function AddOn({ event }) {
  const { addOnItemIds } = useContext(OldCartContext)
  const {
    actions: { selectAddOn, clearAddOn, deleteCartItem },
    chosenAddOn,
    cart
  } = useContext(TicketsAppContext)
  const [upgraded, setUpgraded] = useState(false)
  const [timeObj, selectTime] = useState(null)
  const dialogRef = useRef(null)
  const { name, description, productId, productPhoto, hideProductInventory } =
    event

  let updatedMemberTickets,
    canUpgrade = false
  const activeMembership =
    cart.items.some(item => item.type === 'membership_plan') ||
    (cart.membership && cart.membership.active)
  const selectedEvent = timeObj ? timeObj : event
  const cartAddOn = inCart(selectedEvent, cart)
  const addOnInCart = cartAddOn != null
  const hasMultipleTimesButNoneSelected = !isEmpty(event.times) && !timeObj

  const { time } = DateFormat
  const formatTime = timeToFormat => time(timeToFormat, true).toLowerCase()

  if (inManualUpdate && cartAddOn) {
    const memberPricingTicketsExist = has(cartAddOn.pricing, 'member')
    updatedMemberTickets = cartAddOn && anyMembershipTickets(cartAddOn.tickets)
    canUpgrade =
      activeMembership && !updatedMemberTickets && memberPricingTicketsExist
  }
  const disableButton =
    (addOnInCart && !canUpgrade) || hasMultipleTimesButNoneSelected

  const onChangeTime = time => e => {
    if (e.currentTarget.checked) {
      selectTime(time)
    } else {
      selectTime(null)
    }
  }

  const onOpenDialog = () => _ => {
    if (event.times.length !== 0) {
      if (!timeObj) return
      const selectedEvent = event
      const scheduledEvent = { ...selectedEvent, ...timeObj }
      selectAddOn(scheduledEvent)
    } else {
      selectAddOn(event)
    }
    dialogRef.current.show()
  }

  const onCloseDialog = () => _ => {
    dialogRef.current && dialogRef.current.hide()
    clearAddOn()
  }

  const renderPricing = () => {
    const pricing = extractPricing(event, selectedEvent)
    if (!isEmpty(pricing)) {
      return Object.entries(pricing).map(([key, value], index) => {
        return (
          <PricingBlock
            key={index}
            price={value}
            member={key === 'member'}
          />
        )
      })
    }
  }

  const renderTimes = () => {
    const { productId, times } = event
    let sortedTimes = times.sort((a, b) => a.occursAt - b.occursAt)
    if (times && inCart(event, cart) !== null) {
      if (inManualUpdate) {
        sortedTimes = sortedTimes.filter(time => addOnItemIds.includes(time.id))
      }
      return sortedTimes.map((timeInstance, index) => {
        const selected = timeObj == timeInstance
        return (
          <EventTime
            key={`time-${index}`}
            productId={productId}
            selected={selected}
            availableSpots={timeInstance.availability}
            onSelectTime={onChangeTime(timeInstance)}
            timeInstance={timeInstance}
            hideProductInventory={hideProductInventory}
          />
        )
      })
    }
  }

  const addToCartText = () => {
    let text = `Add ${event.name}`
    if (canUpgrade) {
      text = 'Membership Tickets Available'
    } else if (addOnInCart) {
      setUpgraded(true)
      text = 'Added To Cart'
    }
    return text
  }
  return (
    <React.Fragment>
      <div
        className="bordered-content overflow-hidden"
        data-testid={`addon-${productId}`}
      >
        <div className="lg:flex lg:items-start lg:justify-between">
          <div
            data-label="img-container"
            className="relative overflow-hidden lg:order-1 lg:flex-no-grow lg:flex-shrink-0"
          >
            {/* TODO: Move this to cart logic. */}
            {/*{name == '4D Experience' && <Tag text="Most Popular Upgrade" />}*/}
            <img
              src={productPhoto}
              className="addon-img"
              alt=""
              width="224"
            />
          </div>

          <fieldset>
            <legend
              className="heading-2"
              data-testid={'addon-name'}
            >
              {name}
            </legend>

            <p
              className="text-16 leading-normal"
              dangerouslySetInnerHTML={{
                __html: description
              }}
            >
              {}
            </p>
            <ul className="my-24 text-14">{renderPricing()}</ul>
          </fieldset>
        </div>
        <div>
          {(addOnInCart && !inManualUpdate) || (addOnInCart && upgraded) ? (
            <strong>
              {event.name} {timeObj ? `at ${formatTime(timeObj.occursAt)}` : ''}{' '}
              has been added to your cart!
            </strong>
          ) : (
            <div>
              <ul
                className={
                  hideProductInventory ? 'addons-flex' : 'mb-24 text-18'
                }
              >
                {renderTimes()}
              </ul>
              <button
                data-testid={'add-add-on-to-cart'}
                type="button"
                className={classNames('button -dark -big flex', {
                  '-disabled': disableButton
                })}
                onClick={onOpenDialog()}
                disabled={disableButton}
              >
                <span>{addToCartText()}</span>
              </button>{' '}
            </div>
          )}

          {addOnInCart && !inManualUpdate && (
            <>
              <br />
              <button
                type="button"
                className="text-link mt-16"
                onClick={deleteCartItem.bind(null, cartAddOn.id)}
              >
                Remove
              </button>
            </>
          )}
        </div>
        <A11yDialog
          id={`accessible-dialog-${productId}`}
          dialogRoot="#tickets-dialogs"
          dialogRef={ref => (dialogRef.current = ref)}
          classNames={{
            container: 'dialog',
            overlay: 'dialog-overlay',
            dialog: 'dialog-content',
            title: 'dialog-title',
            closeButton: 'dialog-close'
          }}
          title={name}
        >
          <button
            type="button"
            className="dialog-close"
            onClick={onCloseDialog()}
          >
            <span aria-hidden="true">×</span>
            <span className="sr-only">Close this dialog window</span>
          </button>
          {chosenAddOn && (
            <AddOnParty
              close={onCloseDialog()}
              event={event}
            />
          )}
        </A11yDialog>
      </div>
    </React.Fragment>
  )
}

export default AddOn
